// reportRoute — pure hub-nav mapping from a report TYPE to the page route + href
// used by the jobs-dashboard reports list (Jobs Dashboard Refactor, Increment B /
// S3). The URL shape is the SAME one ReportEditModal's redirectors build:
//   /${route}?borefieldId=<padId>&boreId=<wellboreId>&jobId=<jobId>&reportId=<reportId>
// (borefieldId/boreId are the legacy-named aliases for padId/wellboreId — accepted
// id-identically by the spine resolver). `route === reportType` for MOST types;
// the exceptions below are each verified against app/router.tsx. A type with no
// navigable page returns null (the row renders disabled).
//
// NO FE engineering/economic/statistical math (Charter Abs-2) — pure string map.

import { JOB_REPORT_TYPES } from "../../constants";

/** Types that are modal-only or coming-soon → NO navigable page (row disabled).
 *  - thermal-gradient: rendered inline in ReportEditModal only; no /thermal-gradient route.
 *  - pressure-test: the remaining BOP/FIT/LOT/negative subtypes are "coming soon"
 *    (ReportEditModal returns null); Loop Test was split to `loop-pressure-test`. */
export const NO_PAGE_REPORT_TYPES: ReadonlySet<string> = new Set([
  "thermal-gradient",
  "pressure-test",
]);

/** Types whose route differs from the reportType (verified against router.tsx). */
const ROUTE_OVERRIDES: Readonly<Record<string, string>> = {
  // PO 2026-06-01: the mud log is a page, not a modal — the mud-log report opens
  // the Formation Editor. Router: path "formation-editor".
  "mud-log": "formation-editor",
};

/** Subtype-carrying types: the route is `${base}/${subType}` (router: `safety/:subType`,
 *  `well-control/:subType`). `defaultSub` mirrors ReportEditModal's new-report default;
 *  `valid` gates a report's stored subType so an invalid value can't reach the screen
 *  (which would corrupt the record — see buildReportHref). */
const SUBTYPE_ROUTES: Readonly<
  Record<
    string,
    { base: string; defaultSub: string; valid: ReadonlySet<string> }
  >
> = {
  safety: {
    base: "safety",
    defaultSub: "observation",
    valid: new Set([
      "observation",
      "near-miss",
      "property-damage",
      "spill-leak",
      "injury",
    ]),
  },
  "well-control": {
    base: "well-control",
    defaultSub: "active",
    valid: new Set(["active", "passive", "kill-sheet"]),
  },
};

const KNOWN_REPORT_TYPES: ReadonlySet<string> = new Set(
  JOB_REPORT_TYPES.map((t) => t.value),
);

/** Resolve the route for a report type, optionally pinned to a specific subtype
 *  (subtype-carrying types only). Returns null for an unknown type or a type with
 *  no navigable page. Route carries NO leading slash (buildReportHref adds it). */
function resolveRoute(
  reportType: string,
  subType?: string | null,
): string | null {
  if (!KNOWN_REPORT_TYPES.has(reportType)) return null;
  if (NO_PAGE_REPORT_TYPES.has(reportType)) return null;
  const sub = SUBTYPE_ROUTES[reportType];
  if (sub) {
    // Use the report's real subtype when valid; else the new-report default. A
    // valid subtype is REQUIRED — the screen seeds reportData.subType FROM the
    // route path param and would overwrite (corrupt) a mismatched loaded report.
    const chosen = subType && sub.valid.has(subType) ? subType : sub.defaultSub;
    return `${sub.base}/${chosen}`;
  }
  return ROUTE_OVERRIDES[reportType] ?? reportType;
}

/**
 * The page route (no leading slash) for a report type, or null when the type has
 * no navigable page (modal-only / coming-soon / unknown). For a subtype-carrying
 * type this returns the DEFAULT-subtype route (the new-report route); pass the
 * report's subtype to buildReportHref to target a specific stored report.
 */
export function routeForReportType(reportType: string): string | null {
  return resolveRoute(reportType, undefined);
}

/**
 * As `routeForReportType`, but pinned to a specific subtype for the subtype-carrying
 * families. Exposed for callers that build their OWN query string and so cannot use
 * `buildReportHref` — the drilling timeline, which appends `?at=` (the entry's start
 * time) instead of `reportId`.
 *
 * Shares `resolveRoute` deliberately: the timeline previously kept a THIRD path table
 * (`REPORT_ROUTE` in timeline/constants.ts) that had drifted out of step with this
 * one. An invalid subtype still falls back to the family default here, so the
 * subType-corruption guard documented on `buildReportHref` applies to the timeline too.
 */
export function routeForReportTypeWithSub(
  reportType: string,
  subType?: string | null,
): string | null {
  return resolveRoute(reportType, subType);
}

export interface ReportHrefIds {
  padId?: string | null | undefined;
  wellboreId?: string | null | undefined;
  jobId?: string | null | undefined;
}

/**
 * The full hub-nav href for a report, or null when the type has no navigable page
 * (the row renders disabled). Mirrors ReportEditModal's redirector URL exactly.
 *
 * `subType` (4th arg) is an ADDITIVE extension of the base signature, required for
 * correctness: SafetyScreen / WellControlScreen render the form from the URL
 * `:subType` and SEED reportData.subType from it — opening an existing report at a
 * mismatched subtype route silently overwrites (corrupts) its subType (Charter
 * Abs-7). The reports-list hook passes the report's stored `data.subType`.
 */
export function buildReportHref(
  reportType: string,
  ids: ReportHrefIds,
  reportId?: string | null | undefined,
  subType?: string | null | undefined,
): string | null {
  const route = resolveRoute(reportType, subType);
  if (route === null) return null;
  const params = new URLSearchParams();
  if (ids.padId) params.set("borefieldId", ids.padId);
  if (ids.wellboreId) params.set("boreId", ids.wellboreId);
  if (ids.jobId) params.set("jobId", ids.jobId);
  if (reportId) params.set("reportId", reportId);
  const qs = params.toString();
  return qs ? `/${route}?${qs}` : `/${route}`;
}
